import MedicationIcon from '@mui/icons-material/Medication'
import ScienceIcon from '@mui/icons-material/Science'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import {
  Autocomplete,
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import moment from 'moment'

const MedicineSelectionCard = ({
  drugNames,
  selectedDrug,
  onSelectDrug,
  batchNumbers,
  selectedBatchNumber,
  setSelectedBatchNumber,
}) => {
  const medicineOptions = Object.keys(drugNames).map((medicineID) => ({
    medicineID,
    ...drugNames[medicineID][0],
  }))

  const selectedMedicine =
    medicineOptions.find((med) => med.medicineID === selectedDrug) ?? null

  return (
    <Paper
      variant="outlined"
      sx={{ p: 2, borderRadius: 3, borderColor: 'secondary.main' }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" fontWeight={800} color="anBlue">
            Select Medicine
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose the medicine and batch being administered from this bag.
          </Typography>
        </Box>

        <Autocomplete
          fullWidth
          options={medicineOptions}
          value={selectedMedicine}
          getOptionLabel={(option) => option?.title ?? ''}
          isOptionEqualToValue={(option, value) =>
            option.medicineID === value.medicineID
          }
          onChange={(event, newValue) => {
            onSelectDrug({
              target: {
                value: newValue?.medicineID ?? '',
              },
            })
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Medicine"
              placeholder="Search medicine name"
              required
            />
          )}
          renderOption={(props, med) => (
            <Box component="li" {...props} key={med.medicineID}>
              <Stack
                direction="row"
                spacing={1.5}
                alignItems="center"
                width="100%"
                py={0.5}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    borderRadius: 2,
                    bgcolor: med.isControlled
                      ? 'rgba(211, 47, 47, 0.08)'
                      : 'rgba(0, 107, 79, 0.08)',
                    color: med.isControlled ? 'error.main' : 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {med.isControlled ? <WarningAmberIcon /> : <MedicationIcon />}
                </Box>

                <Box flex={1}>
                  <Typography fontWeight={800}>{med.title}</Typography>

                  <Stack
                    direction="row"
                    spacing={0.75}
                    flexWrap="wrap"
                    mt={0.5}
                  >
                    {med.constitution && (
                      <Chip size="small" label={med.constitution} />
                    )}
                    {med.presentation && (
                      <Chip size="small" label={med.presentation} />
                    )}
                    {med.route && (
                      <Chip
                        size="small"
                        label={med.route}
                        color="primary"
                        variant="outlined"
                      />
                    )}
                    {med.isControlled && (
                      <Chip
                        size="small"
                        label="Controlled Drug"
                        color="error"
                      />
                    )}
                  </Stack>
                </Box>
              </Stack>
            </Box>
          )}
        />

        {selectedDrug && (
          <FormControl fullWidth>
            <InputLabel id="batchNumber-label">Batch Number</InputLabel>
            <Select
              labelId="batchNumber-label"
              value={selectedBatchNumber}
              label="Batch Number"
              onChange={(e) => setSelectedBatchNumber(e.target.value)}
            >
              {Object.keys(batchNumbers).map((bn) => (
                <MenuItem value={bn} key={bn}>
                  <Stack
                    direction="row"
                    spacing={1.5}
                    alignItems="center"
                    width="100%"
                  >
                    <ScienceIcon color="action" />

                    <Box flex={1}>
                      <Typography fontWeight={800}>{bn}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Expires{' '}
                        {moment(batchNumbers[bn][0].expiry).format('MM/YYYY')}
                      </Typography>
                    </Box>

                    <Chip
                      size="small"
                      icon={<Inventory2Icon />}
                      label={`${batchNumbers[bn].length} available`}
                      variant="outlined"
                    />
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </Stack>
    </Paper>
  )
}

export default MedicineSelectionCard
